import {
  CDN_ASSETS,
  localCache,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
  wishlistsApi,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  B2BLoadingPopup,
  BackButton,
  BackToTopButton,
  Header,
  HotelSplitMapView,
  Icon,
  IconName,
  PremierCollectionBenefits,
  WishlistHeaderEntry,
  ensureExhaustive,
  useDeviceTypes,
  wishlistManagementModalActions,
} from "halifax";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouterProps } from "react-router";
import { Link } from "react-router-dom";
import {
  BaseAddress,
  DatelessSearchSource,
  HotelDetailsEntrySourceEnum,
  IIdLodgings,
  ListingSearchResult,
  Lodging,
  LodgingCollectionEnum,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  SELECTED_LC_FROM_LIST,
  SELECTED_LC_FROM_MAP,
  SELECTED_PC_FROM_LIST,
  SELECTED_PC_FROM_MAP,
  SELECTED_VR_FROM_LIST,
  SELECTED_VR_FROM_MAP,
  StayTypesEnum,
  VIEWED_PC_LIST,
  VRAvailabilityResultEnum,
  buildEncodedPersistentLodgingTrackingProperties,
  getNthNightTrackingProperty,
  transformToShopDetailsStringifiedQuery,
} from "redmond";
import { v4 as uuidv4 } from "uuid";
import { ClientContext } from "../../App";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  LC_GATING_EXPERIMENT,
  LUXURY_DATELESS_SEARCH_UNAVAILABLE_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  SHOW_MEALPLAN,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_PC_PRIORITY_SORT_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../context/experiments";
import {
  PATH_AVAILABILITY,
  PATH_HOME,
  PATH_SHOP,
  PATH_VACATION_RENTALS_AVAILABILITY,
  PATH_VACATION_RENTALS_SHOP,
} from "../../utils/paths";
import { RewardsAccountSelection } from "../rewards/components";
import {
  transformToStringifiedAvailabilityQuery,
  transformToStringifiedQuery,
} from "../shop/utils/queryStringHelpers";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { transformToStringifiedVRQuery } from "../vacation-rental-shop/utils/queryStringHelpers";
import { ConnectedWishlistManagementModal } from "../wishlist/WishlistManagementModal/container";
import { WishlistSnackbar } from "../wishlist/WishlistSnackbar/component";
import { AllFiltersModal } from "./components/AvailabilityFilter/components/AllFiltersModal";
import { PremierCollectionAvailabilityList } from "./components/AvailabilityList";
import { AvailabilityMap } from "./components/AvailabilityMap";
import { AvailabilityMapHotelTooltip } from "./components/AvailabilityMap/components/AvailabilityMapHotelTooltip";
import { MobileAvailabilitySearchControl } from "./components/MobileAvailabilitySearchControl";
import { PremierCollectionAvailabilityConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";
import { setIsTravelApp, setIsPCPrioritySortEnabled } from "./actions/actions";
import {
  parseQueryFromString,
  parseQueryString,
} from "./utils/parseQueryString";

export interface IPremierCollectionAvailability
  extends PremierCollectionAvailabilityConnectorProps,
    RouterProps {}

export const PremierCollectionAvailability = (
  props: IPremierCollectionAvailability,
) => {
  const {
    // Context
    history,
    fromDate,
    untilDate,
    stayType,
    setStayType,
    isMapSearch,
    searchLocation,
    hotelQueryParams,
    searchParams,
    hotelsFiltersCount,
    vrFiltersCount,
    // Property selection
    setSelectedListingId,
    selectHome,
    setSelectedLodgingIndex,
    setPropertyIdInFocus,
    focusedProperty,
    // Availability management
    isFetchingInitialAvailability,
    isStillFetchingInFollowUpCalls,
    fetchInitialVacationRentalsAvailability,
    fetchInitialPremierCollectionAvailability,
    // Autocomplete
    // Accounts
    largestValueAccount,
    fetchTravelWalletDetails,
    fetchTravelWalletCreditHistory,
    // Tracking
    viewedPCListProperties,
    trackingProps,
    viewedPremierCollectionListProperties,
    viewedVacationRentalListProperties,
    listPaymentMethods,
  } = props;

  const clientContext = React.useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const [currentUrl, setCurrentUrl] = React.useState(history.location.search);
  const [showExpandedMap, setShowExpandedMap] = React.useState(false);
  const [isFiltersModalOpen, setOpenFiltersModal] =
    React.useState<boolean>(false);
  const locationName = React.useMemo(() => {
    const label = isMapSearch ? "Map area" : searchLocation?.label ?? "";
    return label.split(",")[0];
  }, [searchLocation, isMapSearch]);

  const expState = useExperiments();
  const dispatch = useDispatch();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment],
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  );
  const isTravelCreditHistoryExperiment = React.useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment],
  );

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const isXmodRedesign = getExperimentVariant(
    expState.experiments,
    XMOD_V1_EXPERIMENT,
  ).startsWith(AVAILABLE);

  const VRForPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  );
  const isVRForPremiumCardHoldersEnabled = React.useMemo(
    () => VRForPremiumCardHolders === AVAILABLE,
    [VRForPremiumCardHolders],
  );

  const VRForNonPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  );
  const isVRForNonPremiumCardHoldersEnabled = React.useMemo(
    () => VRForNonPremiumCardHolders === AVAILABLE,
    [VRForNonPremiumCardHolders],
  );

  const isPremiumVR =
    isVRForPremiumCardHoldersEnabled &&
    stayType === StayTypesEnum.VacationRentals;

  const isNonPremiumVR =
    isVRForNonPremiumCardHoldersEnabled &&
    stayType === StayTypesEnum.VacationRentals;

  const isVR = isPremiumVR || isNonPremiumVR;

  const globalMobileNavExperimentVariant = getExperimentVariant(
    expState.experiments,
    GLOBAL_MOBILE_NAV_EXPERIMENT,
  );
  const isGlobalMobileNavExperiment = React.useMemo(
    () => globalMobileNavExperimentVariant === AVAILABLE,
    [globalMobileNavExperimentVariant],
  );

  const cacheHotelTokenVariant = getExperimentVariant(
    expState.experiments,
    CACHE_HOTEL_TOKEN,
  );

  const cacheHotelTokenEnabled = React.useMemo(
    () => cacheHotelTokenVariant === AVAILABLE,
    [cacheHotelTokenVariant],
  );

  const showMealPlanExperimentVariant = getExperimentVariant(
    expState.experiments,
    SHOW_MEALPLAN,
  );

  const isShowMealPlanExperiment = React.useMemo(
    () => showMealPlanExperimentVariant === AVAILABLE,
    [showMealPlanExperimentVariant],
  );

  const vrPCPrioritySortExperimentVariant = getExperimentVariant(
    expState.experiments,
    VR_PC_PRIORITY_SORT_EXPERIMENT,
  );

  const isVRPCPrioritySortEnabled = React.useMemo(
    () => vrPCPrioritySortExperimentVariant === AVAILABLE,
    [vrPCPrioritySortExperimentVariant],
  );

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(isTravelApp);
  }, [isTravelApp]);

  useEffect(() => {
    dispatch(setIsPCPrioritySortEnabled(isVRPCPrioritySortEnabled));
  }, [isVRPCPrioritySortEnabled, dispatch]);

  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST,
  );
  const isWishlistExperiment = React.useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment],
  );

  const datelessSearchUnavailableExperiment = getExperimentVariant(
    expState.experiments,
    LUXURY_DATELESS_SEARCH_UNAVAILABLE_EXPERIMENT,
  );
  const isDatelessSearchUnavailableEnabled = React.useMemo(
    () => datelessSearchUnavailableExperiment === AVAILABLE,
    [datelessSearchUnavailableExperiment],
  );

  const hasWishlists = Boolean(
    useSelector(wishlistsApi.endpoints.getAllWishlists.select())?.data
      ?.wishlists?.length,
  );

  // Initialize wishlist queries when wishlist experiment is enabled
  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  // Initialize wishlist queries when wishlist experiment is enabled
  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  React.useEffect(() => {
    if (stayType === StayTypesEnum.Hotels) {
      if (trackingProps) {
        trackEvent({
          eventName: VIEWED_PC_LIST,
          ...viewedPCListProperties,
        });
      }
    }
  }, [trackingProps, viewedPCListProperties, stayType]);

  React.useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
    // TODO: Find out why window.scrollTo requires a setTimeout; it's needed so that the browser nav button works with scrollTo
    // document.title = SELECT_HOTEL_TITLE;
    // setTimeout(() => window.scrollTo(0, 0), 0);
    // return () => {
    //   document.title = PORTAL_TITLE;
    // };
  }, []);

  const doSearch = ({
    isVRSearch = isVR,
    updateQuery = false,
  }: {
    isVRSearch?: boolean;
    updateQuery?: boolean;
  }) => {
    if (isVRSearch) {
      if (updateQuery) {
        history.push(
          `${PATH_VACATION_RENTALS_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
            {
              location: (searchParams.location?.id as IIdLodgings)
                ?.lodgingSelection.searchTerm,
              fromDate: searchParams.fromDate,
              untilDate: searchParams.untilDate,
              adultsCount: searchParams.adultsCount,
              childrenCount: searchParams.childrenCount,
              petsCount:
                searchParams.type === StayTypesEnum.VacationRentals
                  ? searchParams.petsCount
                  : 0,
            },
          )}`,
        );
      }
      setStayType(StayTypesEnum.VacationRentals);
      fetchInitialVacationRentalsAvailability(history, {
        includePremierCollection: isVRForPremiumCardHoldersEnabled,
      });
    } else {
      if (updateQuery) {
        history.push(
          `${PATH_AVAILABILITY}${transformToStringifiedAvailabilityQuery({
            location: (searchParams.location?.id as IIdLodgings)
              ?.lodgingSelection.searchTerm,
            fromDate: searchParams.fromDate,
            untilDate: searchParams.untilDate,
            adultsCount: searchParams.adultsCount,
            childrenCount: searchParams.childrenCount,
          })}`,
        );
      }
      setStayType(StayTypesEnum.Hotels);
      fetchInitialPremierCollectionAvailability(
        history,
        isLifestyleOnlyCollectionEnabled
          ? LodgingCollectionEnum.Lifestyle
          : LodgingCollectionEnum.Premier,
      );
    }
  };

  React.useEffect(() => {
    if (expState.experiments.length > 0) {
      doSearch({
        isVRSearch:
          history.location.pathname === PATH_VACATION_RENTALS_AVAILABILITY &&
          (isVRForPremiumCardHoldersEnabled ||
            isVRForNonPremiumCardHoldersEnabled),
      });
    }
  }, [history.location.pathname, isVRForPremiumCardHoldersEnabled, expState]);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search],
  );

  React.useEffect(() => {
    const currShowMap = parsedQueryString?.showMap;
    const prevShowMap = parseQueryFromString(currentUrl).showMap;

    if (history.location.search !== currentUrl) {
      if (!isTravelApp || prevShowMap === currShowMap) {
        doSearch({});
        setCurrentUrl(history.location.search);
      }
    }
  }, [history.location.search, currentUrl, isVR]);

  React.useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  useEffect(() => {
    if (parsedQueryString?.isTravelApp) {
      if (parsedQueryString?.showMap === undefined) {
        const searchQueryParams = new URLSearchParams(history.location.search);
        searchQueryParams.set("showMap", showExpandedMap.toString());

        history.replace({
          ...history.location,
          search: searchQueryParams.toString(),
        });
      } else if (
        parsedQueryString?.showMap !== undefined &&
        parsedQueryString?.showMap !== showExpandedMap
      ) {
        setShowExpandedMap(!!parsedQueryString?.showMap);
      }
    }
  }, [
    showExpandedMap,
    parsedQueryString?.showMap,
    !parsedQueryString?.isTravelApp,
  ]);

  const outerFiltersCount = React.useMemo(() => {
    switch (stayType) {
      case StayTypesEnum.Hotels:
        return hotelsFiltersCount;
      case StayTypesEnum.VacationRentals:
        return vrFiltersCount;
      default:
        ensureExhaustive(stayType);
        return 0;
    }
  }, [hotelsFiltersCount, vrFiltersCount]);

  const navigateToProperty = ({
    property,
    index,
    source,
  }: {
    property: Lodging | ListingSearchResult;
    index: number;
    source: "list" | "map";
  }) => {
    if ("lodging" in property) {
      const isLifestyleCollection =
        property.lodgingCollection === LodgingCollectionEnum.Lifestyle;

      const getEventName = () => {
        if (source === "map") {
          return isLifestyleCollection
            ? SELECTED_LC_FROM_MAP
            : SELECTED_PC_FROM_MAP;
        }
        return isLifestyleCollection
          ? SELECTED_LC_FROM_LIST
          : SELECTED_PC_FROM_LIST;
      };

      trackEvent({
        eventName: getEventName(),
        properties: {
          ...viewedPremierCollectionListProperties.properties,
          ...property.trackingPropertiesV2?.properties,
          is_preferred_cot: property.isPreferred,
          lodging_row_index: index,
          nth_night_promo: getNthNightTrackingProperty(property),
          has_pay_at_stay: property.highlights?.hasPayLater,
          is_available: property.available,
        },
        encryptedProperties: [
          ...viewedPremierCollectionListProperties.encryptedProperties,
          property.trackingPropertiesV2?.encryptedProperties ?? "",
        ],
      });
      setSelectedLodgingIndex(index);
      if (property.available) {
        const params = transformToStringifiedQuery({
          lodgingId: property.lodging.id,
          lodgingSelection: searchLocation?.label || "",
          ...hotelQueryParams,
          selectedLodgingIndex: index,
          shopToken: cacheHotelTokenEnabled
            ? property.price?.opaqueShopRequest
            : localCache.set(uuidv4(), property.price?.opaqueShopRequest || "")
                .key,
          encodedPersistentLodgingTrackingProperties:
            buildEncodedPersistentLodgingTrackingProperties(property),
          hotelDetailsEntrySource: source as HotelDetailsEntrySourceEnum,
          isTravelApp,
        });
        if (matchesMobile)
          history.push(`${PATH_SHOP}${params}`, {
            fromPage: history.location.pathname,
          });
        else window.open(`${PATH_SHOP}${params}`, "_blank");
      } else if (isDatelessSearchUnavailableEnabled) {
        // Navigate to /shop-details for unavailable hotels when experiment is ON
        const shopDetailsParams = transformToShopDetailsStringifiedQuery({
          lodgingId: property.lodging.id,
          source: DatelessSearchSource.UnavailableInList,
          unavailable: true,
          fromDate: fromDate ?? undefined,
          untilDate: untilDate ?? undefined,
          isTravelApp,
        });
        if (matchesMobile) {
          history.push({
            pathname: PATH_PREMIER_COLLECTION_SHOP_DETAILS,
            search: shopDetailsParams,
          });
        } else {
          window.open(
            `${PATH_PREMIER_COLLECTION_SHOP_DETAILS}${shopDetailsParams}`,
            "_blank",
          );
        }
      }
    } else {
      const { listingId } = property;
      trackEvent({
        eventName:
          source === "map" ? SELECTED_VR_FROM_MAP : SELECTED_VR_FROM_LIST,
        properties: {
          ...viewedVacationRentalListProperties.properties,
          vr_type: property.listing.listingCollection,
          vr_occupancy: property.listing.terms.maxOccupancy,
          vr_pet_friendly: property.listing.terms.petFriendly,
          vr_market: (
            property?.listing?.content?.location.address as BaseAddress
          )?.city,
          vr_price: property.availability.rate?.price.total.fiat,
          has_reviews: (property.listing.content.userRating?.numberOfReviews ?? 0) > 0,
        },
        encryptedProperties: [
          ...viewedVacationRentalListProperties.encryptedProperties,
        ],
      });
      setSelectedListingId(listingId);
      selectHome(property);
      if (
        property.availability.AvailabilityResult ===
        VRAvailabilityResultEnum.Available
      ) {
        const isBoosted =
          property.listing.trackingProperties?.properties?.is_boosted;
        const params = transformToStringifiedVRQuery({
          listingId: listingId.id,
          location: searchLocation?.label || "",
          ...hotelQueryParams,
          isTravelApp,
          isBoosted,
          selectedLodgingIndex: index,
        });
        if (matchesMobile)
          history.push(`${PATH_VACATION_RENTALS_SHOP}${params}`);
        else window.open(`${PATH_VACATION_RENTALS_SHOP}${params}`, "_blank");
      }
    }
  };

  const renderDesktopView = () => {
    const headerLocationName = isMapSearch ? "map area" : locationName;
    return (
      <HotelSplitMapView
        className="hotel-availability-container"
        header={
          <div>
            <Header
              className="rewards-components-section"
              left={
                <Box className="rewards-account-section-left-content">
                  <Link className="logo" to={PATH_HOME}>
                    {clientContext.logo}
                  </Link>
                  <Box className="rewards-account-section-travel-details">
                    <Typography variant="body1">
                      {isPremierCollectionEnabled
                        ? constants.VIEWING_PREMIUM_STAYS_TEXT(
                            headerLocationName,
                          )
                        : constants.VIEWING_HOTELS_TEXT(headerLocationName)}
                    </Typography>
                    {fromDate && untilDate ? (
                      <Typography variant="body2">
                        {constants.DATES_TEXT(
                          dayjs(fromDate).format("ddd, MMM DD"),
                          dayjs(untilDate).format("ddd, MMM DD"),
                        )}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              }
              right={
                <Box className="desktop-hotel-availability-rewards-account-contents">
                  <RewardsAccountSelection
                    className={clsx("b2b", {
                      "hide-balance-border": isTravelWalletCreditsExperiment,
                    })}
                    popoverClassName="b2b"
                  />
                  {isWishlistExperiment && (
                    <WishlistHeaderEntry
                      hasWishlists={hasWishlists}
                      onClick={() =>
                        dispatch(wishlistManagementModalActions.openModal({}))
                      }
                    />
                  )}
                  {isTravelWalletCreditsExperiment ? (
                    <TravelWalletDrawer />
                  ) : null}
                </Box>
              }
            />
            <PremierCollectionBenefits
              variant={(() => {
                if (isPremierCollectionEnabled)
                  return "availability-with-lifestyle-collection";
                if (isLifestyleOnlyCollectionEnabled)
                  return "availability-only-lifestyle-collection";
                return "availability";
              })()}
              largestValueAccount={largestValueAccount}
              includesVacationRentals={
                isVRForNonPremiumCardHoldersEnabled ||
                isVRForNonPremiumCardHoldersEnabled
              }
              stayType={stayType}
              trackEvent={trackEvent}
              isXmodRedesign={isXmodRedesign}
            />
          </div>
        }
        leftClassName="list-section"
        left={
          <PremierCollectionAvailabilityList
            isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
            fetchInitial={() => doSearch({ updateQuery: true })}
            isWithLifestyleCollection={isPremierCollectionEnabled}
            isWithNonPremiumLifestyleCollection={
              isLifestyleOnlyCollectionEnabled
            }
            navigateToProperty={(property, index) => {
              navigateToProperty({ property, index, source: "list" });
            }}
            isXmodRedesign={isXmodRedesign}
            isDatelessSearchUnavailableEnabled={
              isDatelessSearchUnavailableEnabled
            }
          />
        }
        rightClassName="map-section"
        right={
          <AvailabilityMap
            isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
            isWithNonPremiumLifestyleCollection={
              isLifestyleOnlyCollectionEnabled
            }
            navigateToProperty={(property, index) =>
              navigateToProperty({ property, index, source: "map" })
            }
          />
        }
      />
    );
  };

  const renderMobileView = () => (
    <Box className="hotel-availability-container">
      <Box className="mobile-pc-availability-page">
        {showExpandedMap ? (
          <Box className="mobile-pc-availability-map-wrapper">
            <Box
              className={clsx(
                "mobile-pc-availability-map-container",
                "full-height",
              )}
            >
              <AvailabilityMap
                isVRForPremiumCardHoldersEnabled={
                  isVRForPremiumCardHoldersEnabled
                }
                isWithNonPremiumLifestyleCollection={
                  isLifestyleOnlyCollectionEnabled
                }
                navigateToProperty={(property, index) =>
                  navigateToProperty({ property, index, source: "map" })
                }
              />
              {!isTravelApp && (
                <Box className="map-back-button-container">
                  <BackButton onClick={() => setShowExpandedMap(false)} />
                </Box>
              )}
              <Box className="map-filters-button-container">
                <AllFiltersModal
                  isMobile
                  open={isFiltersModalOpen}
                  setOpen={setOpenFiltersModal}
                  showMealPlanTypeFilter={isShowMealPlanExperiment}
                />
                <ActionButton
                  className="premium-stays-all-filters-modal-button"
                  defaultStyle="h4r-secondary"
                  message={`All Filters${
                    outerFiltersCount > 0 ? ` (${outerFiltersCount})` : ""
                  }`}
                  icon={<Icon name={IconName.Settings} />}
                  onClick={() => setOpenFiltersModal(true)}
                />
              </Box>
            </Box>
            {focusedProperty != null && (
              <Box className="mobile-lodging-availability-map-bottom-items">
                <Box className="mobile-lodging-availability-map-rolling-gallery">
                  <AvailabilityMapHotelTooltip
                    lodging={focusedProperty.property}
                    onClickHotelDetails={(e) => {
                      e.stopPropagation();
                      setPropertyIdInFocus(focusedProperty.id);
                      navigateToProperty({
                        property: focusedProperty.property,
                        index: focusedProperty.index,
                        source: "map",
                      });
                    }}
                    onCloseClick={(e) => {
                      e.stopPropagation();
                      setPropertyIdInFocus(null);
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <MobileAvailabilitySearchControl
              isLifestyleCollection={isLifestyleOnlyCollectionEnabled}
              includesLifestyleCollection={isPremierCollectionEnabled}
              isTravelApp={isTravelApp}
            />
            <Box className="mobile-rewards-account-contents">
              <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
            </Box>

            {!isPremierCollectionEnabled &&
              stayType !== StayTypesEnum.VacationRentals && (
                <PremierCollectionBenefits
                  variant={(() => {
                    if (isLifestyleOnlyCollectionEnabled)
                      return "availability-only-lifestyle-collection";
                    return "availability";
                  })()}
                  isMobile
                  largestValueAccount={largestValueAccount}
                  trackEvent={trackEvent}
                  isXmodRedesign={isXmodRedesign}
                />
              )}
            <Box className={clsx("list-section", { mobile: matchesMobile })}>
              <PremierCollectionAvailabilityList
                fetchInitial={() => {
                  doSearch({});
                }}
                openExpandedMap={() => {
                  if (parsedQueryString?.isTravelApp) {
                    const searchQueryParams = new URLSearchParams(
                      history.location.search,
                    );
                    searchQueryParams.set("showMap", "true");
                    history.push({
                      pathname: history.location.pathname,
                      search: searchQueryParams.toString(),
                    });
                  }
                  setShowExpandedMap(true);
                }}
                navigateToProperty={(property, index) =>
                  navigateToProperty({ property, index, source: "list" })
                }
                isVRForPremiumCardHoldersEnabled={
                  isVRForPremiumCardHoldersEnabled
                }
                isWithLifestyleCollection={isPremierCollectionEnabled}
                isWithNonPremiumLifestyleCollection={
                  isLifestyleOnlyCollectionEnabled
                }
                isXmodRedesign={isXmodRedesign}
                isDatelessSearchUnavailableEnabled={
                  isDatelessSearchUnavailableEnabled
                }
                isMobile
              />
            </Box>
            {isGlobalMobileNavExperiment ? <BackToTopButton /> : undefined}
            {!isGlobalMobileNavExperiment && isTravelWalletCreditsExperiment ? (
              <TravelWalletDrawer />
            ) : undefined}
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <B2BLoadingPopup
        open={isFetchingInitialAvailability || isStillFetchingInFollowUpCalls}
        message={
          isPremierCollectionEnabled || isLifestyleOnlyCollectionEnabled
            ? constants.FETCHING_LC_AVAILABILITY_TEXT
            : constants.FETCHING_AVAILABILITY_TEXT
        }
        secondaryMessage={(() => {
          if (isVR) {
            return constants.FETCHING_VR_AVAILABILITY_SECONDARY_TEXT(
              isPremiumVR,
            );
          }

          if (isPremierCollectionEnabled || isLifestyleOnlyCollectionEnabled) {
            return constants.FETCHING_LC_AVAILABILITY_SECONDARY_TEXT(
              isPremierCollectionEnabled,
            );
          }

          return constants.FETCHING_AVAILABILITY_SECONDARY_TEXT;
        })()}
        image={(() => {
          if (isPremiumVR || isPremierCollectionEnabled) {
            return CDN_ASSETS.capone.searchLoading.premiumStays;
          }
          if (isLifestyleOnlyCollectionEnabled) {
            return CDN_ASSETS.capone.searchLoading.lifestyleCollection;
          }

          return CDN_ASSETS.capone.searchLoading.premierCollection;
        })()}
        className={clsx("premier-collection-search-loading-popup", {
          "with-lifestyle-collection": isPremierCollectionEnabled,
          "lifestyle-collection": isLifestyleOnlyCollectionEnabled,
          xmod: isXmodRedesign,
        })}
        popupSize={matchesMobile ? "mobile" : "desktop"}
        onClose={() => {
          travelAppInterface.sendEvent({
            name: TravelAppInterfaceEventName.SEARCH_LOADED,
            funnel: TravelAppInterfaceFunnelType.PREMIUM_STAYS,
          });
        }}
        isXmod={isXmodRedesign}
      />

      <Box
        className={clsx("pc-hotel-availability-root", {
          "full-width": !matchesMobile,
          mobile: matchesMobile,
          "with-lifestyle-collection": isPremierCollectionEnabled,
          "only-lifestyle-collection": isLifestyleOnlyCollectionEnabled,
          "global-mobile-nav": isGlobalMobileNavExperiment,
          "core-homepage": clientContext.showCoreHomepage,
          "travel-app": isTravelApp,
          xmod: isXmodRedesign,
        })}
      >
        {!matchesMobile ? renderDesktopView() : renderMobileView()}
      </Box>
      {isWishlistExperiment && (
        <>
          <ConnectedWishlistManagementModal
            history={history}
            entryType="premium_stays_availability"
            isTravelApp={isTravelApp}
          />
          <WishlistSnackbar />
        </>
      )}
    </>
  );
};
